import {
  AudienceComparisonCellData,
  AudienceComparisonColumn,
  AudienceComparisonResult,
  audienceComparisonColumns,
} from "../../types/audience-compare";

import { RemoteDataSimple } from "../../types";

const MIN_SCORE_THRESHOLD = 0.1;
const MAX_SCORE_THRESHOLD = 99.9;
const MIN_INDEX_THRESHOLD = 1;
const MAX_INDEX_THRESHOLD = 1500;

const areScoresValid = (baseline: number, audience: number | null): boolean => {
  if (!audience) {
    return false;
  }

  return (
    baseline > MIN_SCORE_THRESHOLD &&
    baseline < MAX_SCORE_THRESHOLD &&
    audience > MIN_SCORE_THRESHOLD &&
    audience < MAX_SCORE_THRESHOLD
  );
};

const isValidIndex = (index: number): boolean => {
  return index > MIN_INDEX_THRESHOLD && index < MAX_INDEX_THRESHOLD;
};

/**
 * Maps a comparison response to a value that can be easily rendered in a table cell.
 *
 * Note: This includes some validation to ensure that scores/indexes are within an acceptable range.
 * If they are NOT within an acceptable range we'll return the `N/A` type.
 * Eventually, this logic will most likely be moved to the backend, but for now,
 * it is faster for us to deploy the front end so we are including it here to iterate faster.
 */
export const mapResponseToCellData = (
  response: RemoteDataSimple<AudienceComparisonResult>,
): AudienceComparisonCellData => {
  if (response.data) {
    const { indexes, baseline, scores, confidence = null } = response.data;
    const score = scores?.[0] || null;
    const index = indexes?.[0];

    const scorePct = score ? score / 100 : null;
    const baselinePct = baseline ? baseline / 100 : null;
    const confidencePct = confidence ? Math.floor(confidence * 100) : null;

    if (index && areScoresValid(baseline, score) && isValidIndex(index)) {
      if (confidencePct) {
        return {
          kind: "alert",
          value: index,
          scorePct,
          baselinePct,
          confidencePct,
        };
      } else {
        return { kind: "success", value: index, scorePct, baselinePct };
      }
    } else {
      return { kind: "n-a" };
    }
  }
  return response.error ? { kind: "error" } : { kind: "loading" };
};

export const parseColumn = (x: unknown): x is AudienceComparisonColumn => {
  return (
    typeof x === "string" &&
    audienceComparisonColumns.includes(x as AudienceComparisonColumn)
  );
};

/**
 * Parses an array of unknown values into an array of valid `AudienceComparisonColumn` values.
 * This will remove any invalid values and return a fallback if no valid values are found.
 */
export const parseColumns = (
  xs: unknown[],
  fallback: AudienceComparisonColumn[] = ["index"],
): AudienceComparisonColumn[] => {
  const validColumns = xs.filter(parseColumn);
  return validColumns.length > 0 ? validColumns : fallback;
};
